import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  CardComponent,
  CardContentComponent,
  CardHeaderComponent,
  IconComponent,
} from '@gains-com/ui-angular';
import { MatButton, MatIconButton } from '@angular/material/button';
import { AuthService } from '../../core/auth.service.js';
import { EnvironmentPipe } from '../../pipes/environment.pipe.js';
import { KycWizardComponent } from '../kyc-wizard/kyc-wizard.component.js';
import { LOGGER_FACTORY } from '../../core/logger/logger-factory.provider.js';
import { KycVerificationViewService } from '../../core/view-states/kyc-verification-view.service.js';
import { toSignal } from '@angular/core/rxjs-interop';
import { Analytics } from '../../core/analytics/analytics.service.js';
import { AnalyticsEvent } from '@gains-com/shared';
import type { KycDialogData, KycDialogResult, KycStatus } from './kyc-dialog.types.js';

@Component({
  selector: 'app-kyc-dialog',
  standalone: true,
  imports: [
    CardComponent,
    CardHeaderComponent,
    CardContentComponent,
    IconComponent,
    MatIconButton,
    MatButton,
    EnvironmentPipe,
    KycWizardComponent,
  ],
  templateUrl: './kyc-dialog.component.html',
  styleUrl: './kyc-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KycDialogComponent {
  private readonly _dialogRef = inject(DialogRef<KycDialogResult>);
  private readonly _data = inject<KycDialogData | null>(DIALOG_DATA, {
    optional: true,
  });
  private readonly _auth = inject(AuthService);
  private readonly _kycView = inject(KycVerificationViewService);
  private readonly _analytics = inject(Analytics);
  private readonly _logger = inject(LOGGER_FACTORY).create('KycDialog');

  kycStatus = signal<KycStatus>('none');
  sessionRefreshRequired = signal(false);
  autoClose = signal(this._data?.autoClose ?? false);
  currentKyc = toSignal(this._kycView.kycVerification$);

  async onKycStatusChange(status: KycStatus) {
    this._logger.info('KYC status changed', { status });
    this.kycStatus.set(status);

    if (status === 'approved') {
      const tokenSynced = await this._checkKycTokenSync();
      if (!tokenSynced) {
        this.sessionRefreshRequired.set(true);
        return;
      }

      if (this.autoClose()) {
        this._dialogRef.close('verified');
      }
    } else if (status === 'rejected') {
      // Keep dialog open for retry option
    }
  }

  onKycContinue() {
    this._dialogRef.close('verified');
  }

  onVerifyLater() {
    const status = this.kycStatus();
    if (status === 'approved') {
      this._dialogRef.close('verified');
    } else if (status === 'pending') {
      this._dialogRef.close('pending');
    } else {
      this._dialogRef.close('cancelled');
    }
  }

  close() {
    const status = this.kycStatus();
    if (status === 'approved') {
      this._dialogRef.close('verified');
    } else if (status === 'pending') {
      this._dialogRef.close('pending');
    } else if (status === 'rejected') {
      this._dialogRef.close('rejected');
    } else {
      this._dialogRef.close('cancelled');
    }
  }

  async logout() {
    await this._auth.signOut();
    this._dialogRef.close('session-refresh-required');
  }

  private async _checkKycTokenSync(): Promise<boolean> {
    try {
      const dbKyc = this.currentKyc();

      if (dbKyc?.kyc_verification_status !== 'approved') {
        this._logger.debug(
          'KYC not approved or not loaded, skipping sync check'
        );
        return true;
      }

      const tokenKycVerified = await this._auth.kycVerified;

      if (tokenKycVerified) {
        this._logger.debug('KYC token is in sync');
        return true;
      }

      this._logger.warn('KYC token mismatch detected', {
        dbApproved: dbKyc.kyc_verification_status === 'approved',
        tokenVerified: tokenKycVerified,
      });

      this._logger.info('Attempting token refresh...');
      const { token } = await this._auth.forceUserReload();
      const refreshedTokenKyc = token?.claims?.['kyc_verified'] === true;

      if (refreshedTokenKyc) {
        this._logger.info('KYC token successfully refreshed');
        return true;
      }

      this._logger.error('KYC token still mismatched after refresh', {
        claims: token?.claims,
      });

      this._analytics.trackEvent(AnalyticsEvent.KYCVerifiedTokenMismatch, {
        kyc_verified_in_db: dbKyc.kyc_verification_status === 'approved',
        kyc_verified_on_token: refreshedTokenKyc,
        kyc_verification_id: dbKyc.kyc_verification_id,
      });

      return false;
    } catch (error: any) {
      this._logger.error('Failed to check/refresh KYC token', {
        error: error.message,
        stack: error.stack,
      });
      return false;
    }
  }
}
